/**
 * Delivery Control Context
 * 
 * Orchestration layer that translates reconciliation intelligence
 * into concrete, proportional execution adjustments.
 * 
 * This context:
 * - Consumes ReconciliationContext
 * - Computes control signals via pure logic
 * - Exposes helper functions for downstream systems
 * - Logs applied signals in development mode
 * 
 * It does NOT:
 * - Route leads
 * - Mutate contracts
 * - Override auctions
 */

import { createContext, useContext, useMemo, useEffect, ReactNode } from 'react';
import { useReconciliation } from './ReconciliationContext';
import {
  computeControlSignals,
  getRetryPriorityMultiplier as _getRetryPriorityMultiplier,
  getPoolBoost as _getPoolBoost,
  getGuaranteedMinOverride as _getGuaranteedMinOverride,
  getPacingCap as _getPacingCap,
  hasCriticalWarnings,
  getAgentsWithActiveSignals,
  serializeControlSignals,
  type ControlSignals,
  type ControlConfig,
  type SystemWarning,
  DEFAULT_CONTROL_CONFIG,
} from '@/lib/deliveryControl';

// Re-export types for consumers
export type { ControlSignals, ControlConfig, SystemWarning };

// --- Context State ---

interface DeliveryControlState {
  /** Current computed control signals */
  signals: ControlSignals;
  /** Whether any critical warnings exist */
  hasCriticalWarnings: boolean;
  /** List of agents with active control signals */
  activeAgents: string[];
  /** Timestamp of last signal computation */
  lastComputedAt: number | null;
  /** Current configuration */
  config: ControlConfig;
}

interface DeliveryControlActions {
  /** Get retry priority multiplier for an agent (1.0 = normal) */
  getRetryPriorityMultiplier: (agentId: string) => number;
  /** Get pool inclusion boost for an agent (0 = no boost) */
  getPoolBoost: (agentId: string) => number;
  /** Get guaranteed minimum override for an agent (undefined = no override) */
  getGuaranteedMinOverride: (agentId: string) => number | undefined;
  /** Get pacing cap for an agent (1.0 = no cap) */
  getPacingCap: (agentId: string) => number;
  /** Check if an agent has any active control signals */
  hasActiveSignals: (agentId: string) => boolean;
  /** Get all warnings for an agent */
  getAgentWarnings: (agentId: string) => SystemWarning[];
  /** Get serializable state for logging/debugging */
  getDebugState: () => Record<string, unknown>;
}

// --- Context ---

const DeliveryControlContext = createContext<{
  state: DeliveryControlState;
  actions: DeliveryControlActions;
} | undefined>(undefined);

// --- Provider ---

interface DeliveryControlProviderProps {
  children: ReactNode;
  /** Custom configuration (optional) */
  config?: Partial<ControlConfig>;
  /** Enable development logging */
  enableLogging?: boolean;
}

export function DeliveryControlProvider({
  children,
  config: customConfig,
  enableLogging = process.env.NODE_ENV === 'development',
}: DeliveryControlProviderProps) {
  const { state: reconciliationState } = useReconciliation();
  
  // Merge custom config with defaults
  const config = useMemo(() => ({
    ...DEFAULT_CONTROL_CONFIG,
    ...customConfig,
  }), [customConfig]);
  
  // Compute control signals from reconciliation results
  const signals = useMemo(() => {
    return computeControlSignals(reconciliationState.lastResult, config);
  }, [reconciliationState.lastResult, config]);
  
  // Derived state
  const criticalWarnings = useMemo(() => hasCriticalWarnings(signals), [signals]);
  const activeAgents = useMemo(() => getAgentsWithActiveSignals(signals), [signals]);
  
  // Log signals in development mode
  useEffect(() => {
    if (!enableLogging) return;
    if (!reconciliationState.lastResult) return;
    
    const serialized = serializeControlSignals(signals);
    
    if (activeAgents.length > 0 || signals.systemWarnings.length > 0) {
      console.log('[DeliveryControl] Control signals computed:', serialized);
      
      if (criticalWarnings) {
        console.warn('[DeliveryControl] CRITICAL WARNINGS:', 
          signals.systemWarnings.filter(w => w.severity === 'critical')
        );
      }
    }
  }, [signals, enableLogging, activeAgents.length, criticalWarnings, reconciliationState.lastResult]);
  
  // --- Action Implementations ---
  
  const getRetryPriorityMultiplier = useMemo(() => {
    return (agentId: string) => _getRetryPriorityMultiplier(signals, agentId);
  }, [signals]);
  
  const getPoolBoost = useMemo(() => {
    return (agentId: string) => _getPoolBoost(signals, agentId);
  }, [signals]);
  
  const getGuaranteedMinOverride = useMemo(() => {
    return (agentId: string) => _getGuaranteedMinOverride(signals, agentId);
  }, [signals]);
  
  const getPacingCap = useMemo(() => {
    return (agentId: string) => _getPacingCap(signals, agentId);
  }, [signals]);
  
  const hasActiveSignals = useMemo(() => {
    return (agentId: string) => activeAgents.includes(agentId);
  }, [activeAgents]);
  
  const getAgentWarnings = useMemo(() => {
    return (agentId: string) => signals.systemWarnings.filter(
      w => w.agentId === agentId
    );
  }, [signals]);
  
  const getDebugState = useMemo(() => {
    return () => serializeControlSignals(signals);
  }, [signals]);
  
  // --- Build Context Value ---
  
  const state: DeliveryControlState = useMemo(() => ({
    signals,
    hasCriticalWarnings: criticalWarnings,
    activeAgents,
    lastComputedAt: signals.computedAt,
    config,
  }), [signals, criticalWarnings, activeAgents, config]);
  
  const actions: DeliveryControlActions = useMemo(() => ({
    getRetryPriorityMultiplier,
    getPoolBoost,
    getGuaranteedMinOverride,
    getPacingCap,
    hasActiveSignals,
    getAgentWarnings,
    getDebugState,
  }), [
    getRetryPriorityMultiplier,
    getPoolBoost,
    getGuaranteedMinOverride,
    getPacingCap,
    hasActiveSignals,
    getAgentWarnings,
    getDebugState,
  ]);
  
  return (
    <DeliveryControlContext.Provider value={{ state, actions }}>
      {children}
    </DeliveryControlContext.Provider>
  );
}

// --- Hook ---

export function useDeliveryControl() {
  const context = useContext(DeliveryControlContext);
  if (!context) {
    throw new Error('useDeliveryControl must be used within a DeliveryControlProvider');
  }
  return context;
}

// --- Convenience Hooks ---

/**
 * Hook to get control signals for a specific agent
 */
export function useAgentControlSignals(agentId: string) {
  const { actions } = useDeliveryControl();
  
  return useMemo(() => ({
    retryPriorityMultiplier: actions.getRetryPriorityMultiplier(agentId),
    poolBoost: actions.getPoolBoost(agentId),
    guaranteedMinOverride: actions.getGuaranteedMinOverride(agentId),
    pacingCap: actions.getPacingCap(agentId),
    hasActiveSignals: actions.hasActiveSignals(agentId),
    warnings: actions.getAgentWarnings(agentId),
  }), [agentId, actions]);
}

/**
 * Hook to check system health
 */
export function useSystemHealth() {
  const { state } = useDeliveryControl();
  
  return useMemo(() => ({
    hasCriticalWarnings: state.hasCriticalWarnings,
    activeAgentCount: state.activeAgents.length,
    warnings: state.signals.systemWarnings,
    lastComputedAt: state.lastComputedAt,
  }), [state]);
}

export default DeliveryControlContext;
